import m from 'mithril'
import { authModel } from './models/auth.js'
import { adminAuthModel } from './models/admin-auth.js'
import { Layout } from './components/layout.js'
import { LoginView } from './views/login.js'
import { ProjectsView } from './views/projects.js'
import { ProjectView } from './views/project.js'
import { UploadView } from './views/upload.js'
import { BrowserView } from './views/browser.js'
import { SessionView } from './views/session.js'
import { TagsView } from './views/tags.js'
import { RubricsView } from './views/rubrics.js'
import { SettingsView } from './views/settings.js'
import { TasksView } from './views/tasks.js'
import { TaskDetailView } from './views/task-detail.js'
import { GuestTaskView } from './views/guest-task.js'
import { ClustersView } from './views/clusters.js'
import { ClusterDetailView } from './views/cluster-detail.js'
import { BulkHighlightsView } from './views/bulk-highlights.js'
import { PredictionsView } from './views/predictions.js'
import { PiiDetectionView } from './views/pii-detection.js'
import { ExportView } from './views/export.js'
import { PropagateView } from './views/propagate.js'
import { NotificationsView } from './views/notifications.js'
import { ResetPasswordView } from './views/reset-password.js'
import { AdminLoginView } from './views/admin-login.js'
import { AdminView } from './views/admin.js'
import { JoinView } from './views/join.js'
import { EvalSummaryView } from './views/eval-summary.js'
import { StylesView } from './views/styles.js'
import { ScenarioPacksView } from './views/scenario-packs.js'
import { PackView } from './views/pack-view.js'
import { ScenarioWizardView } from './views/scenario-wizard.js'
import { ScenarioWriterView } from './views/scenario-writer.js'
import { NotFoundView } from './components/error-boundary.js'

// Import theme (triggers injectGlobal)
import './styles/theme.js'

// --- Route guard: redirect to login if not authenticated ---
function requireAuth(component) {
  return {
    async onmatch() {
      if (!authModel.initialized) {
        await authModel.init()
      }
      if (!authModel.isLoggedIn && !authModel.loading) {
        m.route.set('/login')
      }
    },
    render(vnode) {
      if (authModel.loading) return m(Layout, m('div', 'Loading...'))
      if (!authModel.isLoggedIn) return null
      return m(Layout, m(component, vnode.attrs))
    },
  }
}

function guestOnly(component) {
  return {
    async onmatch() {
      if (!authModel.initialized) {
        await authModel.init()
      }
      if (authModel.isLoggedIn) {
        m.route.set('/projects')
      }
    },
    render(vnode) {
      if (authModel.loading) return m(Layout, m('div', 'Loading...'))
      return m(Layout, m(component, vnode.attrs))
    },
  }
}

// Admin routes use a separate auth model
function requireAdminAuth(component) {
  return {
    async onmatch() {
      if (adminAuthModel.loading) return
      if (!adminAuthModel.isLoggedIn) {
        // Lazy-init: check admin session on first visit
        await adminAuthModel.init()
        if (!adminAuthModel.isLoggedIn) {
          m.route.set('/admin/login')
        }
      }
    },
    render(vnode) {
      if (adminAuthModel.loading) return m(Layout, m('div', 'Loading...'))
      if (!adminAuthModel.isLoggedIn) return null
      return m(Layout, m(component, vnode.attrs))
    },
  }
}

// --- Placeholder views for future phases ---
function Placeholder(label) {
  return () => ({
    view() {
      return m('div', { style: 'padding: 2rem; text-align: center; color: #999;' }, [
        m('h2', label),
        m('p', 'Coming soon'),
      ])
    },
  })
}

// --- Routes ---
const routes = {
  '/': {
    async onmatch() {
      if (!authModel.initialized) {
        await authModel.init()
      }
      if (authModel.isLoggedIn) {
        m.route.set('/projects')
      } else {
        m.route.set('/login')
      }
    },
    render() {
      return m(Layout, m('div', 'Loading...'))
    },
  },
  '/login': guestOnly(LoginView),
  '/reset-password': {
    render(vnode) {
      return m(Layout, m(ResetPasswordView, vnode.attrs))
    },
  },
  '/admin/login': {
    render(vnode) {
      return m(Layout, m(AdminLoginView, vnode.attrs))
    },
  },
  '/admin': requireAdminAuth(AdminView),
  '/projects': requireAuth(ProjectsView),
  '/projects/:projectId': requireAuth(ProjectView),
  '/projects/:projectId/browser': requireAuth(BrowserView),
  '/projects/:projectId/upload': requireAuth(UploadView),
  '/projects/:projectId/tags': requireAuth(TagsView),
  '/projects/:projectId/rubrics': requireAuth(RubricsView),
  '/projects/:projectId/settings': requireAuth(SettingsView),
  '/projects/:projectId/tasks': requireAuth(TasksView),
  '/projects/:projectId/tasks/:taskId': requireAuth(TaskDetailView),
  '/projects/:projectId/clusters': requireAuth(ClustersView),
  '/projects/:projectId/clusters/:clusterId': requireAuth(ClusterDetailView),
  '/projects/:projectId/notifications': requireAuth(NotificationsView),
  '/projects/:projectId/export': requireAuth(ExportView),
  '/projects/:projectId/predictions': requireAuth(PredictionsView),
  '/projects/:projectId/bulk-highlights': requireAuth(BulkHighlightsView),
  '/projects/:projectId/pii-detection': requireAuth(PiiDetectionView),
  '/projects/:projectId/eval-summary': requireAuth(EvalSummaryView),
  '/projects/:projectId/styles': requireAuth(StylesView),
  '/projects/:projectId/scenarios': requireAuth(ScenarioPacksView),
  '/projects/:projectId/scenarios/:packId': requireAuth(PackView),
  '/projects/:projectId/scenarios/:packId/:scenarioId/write': requireAuth(ScenarioWriterView),
  '/projects/:projectId/scenarios/:packId/:scenarioId/remix/:generationId': requireAuth(ScenarioWriterView),
  '/projects/:projectId/scenarios/:packId/:scenarioId/:step': requireAuth(ScenarioWizardView),
  '/projects/:projectId/sessions/:sessionId': requireAuth(SessionView),
  '/projects/:projectId/propagate/:sourceSessionId': requireAuth(PropagateView),
  '/join/:code': {
    async onmatch() {
      if (!authModel.initialized) await authModel.init()
    },
    render(vnode) {
      return m(Layout, m(JoinView, vnode.attrs))
    },
  },
  '/guest/:projectId/:token': {
    render(vnode) {
      return m(GuestTaskView, vnode.attrs)
    },
  },
  '/:path...': {
    render() {
      return m(Layout, m(NotFoundView))
    },
  },
}

// --- Boot ---
async function boot() {
  const hash = window.location.hash || ''
  const isAdminRoute = hash.startsWith('#!/admin')

  // Only check user session on non-admin routes.
  // User auth lazy-inits via requireAuth/guestOnly when needed.
  if (!isAdminRoute) {
    await authModel.init()
  }

  const root = document.getElementById('app')
  m.route(root, '/', routes)
}

boot()
